import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import AffinLogo from '~assets/images/affin-logo-v1.png';
import { TGetExportDailyBalancingReportData } from '~types/feat/dashboard';
import { MovementType } from '~utils/enums';
import UtilFunctions from '~utils/functions';

type DailyBalancingPDFProps = {
    data?: TGetExportDailyBalancingReportData;
};

const DailyBalancingPDF = ({ data }: DailyBalancingPDFProps) => {
    const { Misc: { dateTimeFormat } } = UtilFunctions;

    type Movement = TGetExportDailyBalancingReportData['movements'][number];

    const formatGram = (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === '') return '';

        const amount = Number(value);

        if (!Number.isFinite(amount)) return '';

        return amount.toLocaleString('en-US', {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
        });
    };

    const getTransactionDescription = (item: Movement) => {
        const movementName = item.movementTypeName.replace(/^(Approved|Cancelled)/, '').replace(/([a-z])([A-Z])/g, '$1 $2');

        const name = item.isEarmark ? `${movementName} (Earmark)` : movementName;

        const isStockMovement = [
            MovementType.ApprovedStockIn,
            MovementType.CancelledStockIn,
            MovementType.ApprovedStockOut
        ].includes(item.movementType);

        if (isStockMovement) {
            return item.serialNumbers?.length ? `${name}\n${item.serialNumbers.map(serial => `#${serial}`).join(', ')}` : name;
        }

        if (item.conversionId) {
            return `${name}\nConversion ID #${item.conversionId}`;
        }

        if (item.transactionId) {
            return `${name}\nTransaction ID #${item.transactionId}`;
        }

        return name;
    };

    return (
        <Document>
            <Page size='A4' style={pdfStyles.page}>
                <View>
                    <Image
                        style={pdfStyles.headerLogo}
                        src={AffinLogo}
                    />
                    <Text style={pdfStyles.headerTitle}>DAILY BALANCING REPORT</Text>

                    <View style={pdfStyles.headerDetailsContainer}>
                        <View style={pdfStyles.headerDetails}>
                            <Text style={pdfStyles.text}>Product Name</Text>
                            <Text style={pdfStyles.subText}>
                                {data?.productName}
                            </Text>
                        </View>

                        <View style={pdfStyles.headerDetails}>
                            <Text style={pdfStyles.text}>Report Period</Text>

                            <View style={pdfStyles.dateContainer}>
                                <Text style={pdfStyles.subText}>
                                    {data?.periodStart ? dateTimeFormat(data.periodStart) : '-'}
                                </Text>
                                <Text style={pdfStyles.subText}>to</Text>
                                <Text style={pdfStyles.subText}>
                                    {data?.periodEnd ? dateTimeFormat(data.periodEnd) : '-'}
                                </Text>
                            </View>
                        </View>

                        <View style={pdfStyles.headerDetails}>
                            <Text style={pdfStyles.text}>Branch</Text>
                            <Text style={pdfStyles.subText}>
                                {data?.branchName}
                            </Text>
                        </View>
                    </View>

                    <View style={pdfStyles.table}>
                        <View style={pdfStyles.tableRow} wrap={false}>
                            <Text style={[pdfStyles.tableHeader, pdfStyles.dateCell]}>
                                Date
                            </Text>

                            <Text style={[pdfStyles.tableHeader, pdfStyles.transactionCell]}>
                                Transaction
                            </Text>

                            <Text style={[pdfStyles.tableHeader, pdfStyles.amountCell]}>
                                Debit (gram)
                            </Text>

                            <Text style={[pdfStyles.tableHeader, pdfStyles.amountCell]}>
                                Credit (gram)
                            </Text>

                            <Text style={[pdfStyles.tableHeader, pdfStyles.balanceCell]}>
                                Balance (gram)
                            </Text>
                        </View>

                        <View style={pdfStyles.tableRow} wrap={false}>
                            <Text style={[pdfStyles.tableCell, pdfStyles.dateCell]}>
                                {dayjs(data?.periodStart).format('HH:mm')}
                                {'\n'}
                                {dayjs(data?.periodStart).format('DD MMMM YYYY').toUpperCase()}
                            </Text>

                            <Text style={[pdfStyles.tableCell, pdfStyles.transactionCell]}>
                                Starting Balance
                            </Text>

                            <Text style={[pdfStyles.tableCell, pdfStyles.amountCell]} />

                            <Text style={[pdfStyles.tableCell, pdfStyles.amountCell]} />

                            <Text
                                style={[
                                    pdfStyles.tableCell,
                                    pdfStyles.balanceCell,
                                    pdfStyles.numberCell,
                                ]}
                            >
                                {formatGram(data?.startingBalance)}
                            </Text>
                        </View>

                        {data?.movements.map(item => (
                            <View key={item.id} style={pdfStyles.tableRow} wrap={false}>
                                <Text style={[pdfStyles.tableCell, pdfStyles.dateCell]}>
                                    {dayjs(item.createdAt).format('HH:mm')}
                                    {'\n'}
                                    {dayjs(item.createdAt).format('DD MMMM YYYY').toUpperCase()}
                                </Text>

                                <Text style={[pdfStyles.tableCell, pdfStyles.transactionCell]}>
                                    {getTransactionDescription(item)}
                                </Text>

                                <Text style={[pdfStyles.tableCell, pdfStyles.amountCell, pdfStyles.numberCell]}>
                                    {formatGram(item.debit)}
                                </Text>

                                <Text style={[pdfStyles.tableCell, pdfStyles.amountCell, pdfStyles.numberCell]}>
                                    {formatGram(item.credit)}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.tableCell,
                                        pdfStyles.balanceCell,
                                        pdfStyles.numberCell,
                                    ]}
                                >
                                    {formatGram(item.balance)}
                                </Text>
                            </View>
                        ))}
                    </View>
                    {/* Summary */}
                    <View style={pdfStyles.summaryContainer}>
                        <View style={pdfStyles.summaryColumn}>
                            <View style={pdfStyles.summaryRow}>
                                <Text>Total Debit</Text>
                                <Text>{formatGram(data?.totalDebit)}</Text>
                            </View>

                            <View style={pdfStyles.summaryRow}>
                                <Text>Total Credit</Text>
                                <Text>{formatGram(data?.totalCredit)}</Text>
                            </View>
                        </View>

                        <View style={pdfStyles.summaryColumn}>
                            <View style={pdfStyles.summaryRow}>
                                <Text>Closing Balance</Text>
                                <Text>{formatGram(data?.closingBalance)}</Text>
                            </View>

                            <View style={pdfStyles.summaryRow}>
                                <Text>Floating Balance</Text>
                                <Text>{formatGram(data?.floatingBalance)}</Text>
                            </View>
                        </View>
                    </View>

                    {/* Date Issued */}
                    <View style={pdfStyles.dateIssuedContainer}>
                        <Text>Date Issued</Text>
                        <Text>
                            {dayjs(data?.businessDate).format('DD MMMM YYYY').toUpperCase()}
                        </Text>
                    </View>

                    {/* Footer */}
                    <View style={pdfStyles.footerContainer}>
                        <View>
                            <Text>Affin Bank Berhad</Text>
                            <Text>Confidential</Text>
                        </View>

                        <View style={pdfStyles.footerRight}>
                            <Text>
                                This report was generated on {dayjs(data?.generatedAt).format('D MMMM YYYY [at] HH:mm')}.
                            </Text>
                            <Text>
                                All date and times displayed are in MYT (GMT +8).
                            </Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

const pdfStyles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 12,
        fontFamily: 'Helvetica',
    },
    headerLogo: {
        width: 162,
        height: 50,
        padding: 5,
    },
    headerTitle: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        marginTop: 8,
    },
    headerDetailsContainer: {
        width: '100%',
        alignItems: 'flex-end',
        marginTop: 15,
        marginBottom: 20,
    },
    headerDetails: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 280,
        marginBottom: 8,
    },
    text: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 8,
        width: '45%',
    },
    subText: {
        fontFamily: 'Helvetica',
        fontSize: 8,
        width: '55%',
        textAlign: 'right',
    },
    title: {
        fontSize: 18,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 20,
    },
    dateContainer: {
        flexDirection: 'column',
        alignItems: 'flex-end',
        width: '55%',
        gap: 4,
    },
    table: {
        width: '100%',
        borderTopWidth: 1,
        borderLeftWidth: 1,
        borderColor: '#000',
        marginTop: 10,
    },
    tableRow: {
        flexDirection: 'row',
    },
    tableHeader: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 8,
        padding: 5,
        minHeight: 32,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
    },
    tableCell: {
        fontFamily: 'Helvetica',
        fontSize: 8,
        padding: 5,
        minHeight: 38,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
    },
    dateCell: {
        width: '21%',
    },
    transactionCell: {
        width: '31%',
    },
    amountCell: {
        width: '14%',
    },
    balanceCell: {
        width: '20%',
    },
    numberCell: {
        textAlign: 'right',
        paddingTop: 12,
    },
    summaryContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 15,
        width: '100%',
    },
    summaryColumn: {
        width: '40%',
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 5,
        fontSize: 9,
    },
    dateIssuedContainer: {
        flexDirection: 'row',
        gap: 70,
        marginTop: 20,
        fontSize: 9,
    },
    footerContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 35,
        fontSize: 8,
    },
    footerRight: {
        alignItems: 'flex-end',
        textAlign: 'right',
    },
});

export default DailyBalancingPDF;